import { useState, useEffect } from "react";
import { auth } from "./firebase";
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";

const provider = new GoogleAuthProvider();

// Turn Firebase's ugly error codes into human messages
const friendlyError = (e) => {
  const code = e?.code || "";
  if (code.includes("invalid-credential") || code.includes("wrong-password") || code.includes("user-not-found"))
    return "Wrong email or password. Try again or reset your password.";
  if (code.includes("invalid-email")) return "That doesn't look like a valid email.";
  if (code.includes("email-already-in-use")) return "An account with this email already exists. Try signing in.";
  if (code.includes("weak-password")) return "Password needs to be at least 6 characters.";
  if (code.includes("too-many-requests")) return "Too many attempts. Wait a minute and try again.";
  if (code.includes("popup-closed-by-user")) return "Google sign-in was closed before finishing.";
  if (code.includes("network-request-failed")) return "Network error. Check your connection and try again.";
  return "Something went wrong. Please try again.";
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    getRedirectResult(auth).then((cred) => {
      if (cred) {
        const isNew = cred?._tokenResponse?.isNewUser;
        if (isNew) sessionStorage.setItem("haulmate_just_signedup", cred.user.uid);
      }
    }).catch((e) => {
      setError(friendlyError(e));
    });
  }, []);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const handleGoogle = async () => {
    setLoading(true);
    try {
      // Detect if running as PWA (standalone mode)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      if (isStandalone) {
        await signInWithRedirect(auth, provider);
        return;
      }
      const cred = await signInWithPopup(auth, provider);
      const isNew = cred?._tokenResponse?.isNewUser;
      if (isNew) sessionStorage.setItem("haulmate_just_signedup", cred.user.uid);
    } catch (e) {
      setError(friendlyError(e));
      triggerShake();
    }
    setLoading(false);
  };

  const handleEmail = async () => {
    setLoading(true);
    try {
      if (isSignUp) {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        sessionStorage.setItem("haulmate_just_signedup", cred.user.uid);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (e) {
      setError(friendlyError(e));
      triggerShake();
    }
    setLoading(false);
  };

  const handleReset = async () => {
    if (!email.trim()) {
      setError("Enter your email above first, then tap reset.");
      triggerShake();
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setResetSent(true);
      setError("");
    } catch (e) {
      setError(friendlyError(e));
      triggerShake();
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0a0a",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "32px 24px",
      fontFamily: "'DM Sans', sans-serif",
      color: "#f5f0e0",
      maxWidth: 430,
      width: "100%",
      margin: "0 auto",
      boxSizing: "border-box",
      overflowX: "hidden",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        .fade-1 { animation: fadeSlideUp 0.5s ease forwards; }
        .fade-2 { animation: fadeSlideUp 0.5s ease 0.1s forwards; opacity: 0; }
        .fade-3 { animation: fadeSlideUp 0.5s ease 0.2s forwards; opacity: 0; }
        .fade-4 { animation: fadeSlideUp 0.5s ease 0.3s forwards; opacity: 0; }
        .fade-5 { animation: fadeSlideUp 0.5s ease 0.4s forwards; opacity: 0; }
        .fade-6 { animation: fadeSlideUp 0.5s ease 0.5s forwards; opacity: 0; }
        .logo-float { animation: logoFloat 3s ease-in-out infinite; }
        .shake { animation: shake 0.6s ease; }
        .inp-login {
          background: #0d0e00;
          border: 1px solid #4a3800;
          border-radius: 12px;
          padding: 13px 16px;
          color: #f5f0e0;
          font-size: 15px;
          width: 100%;
          outline: none;
          font-family: 'DM Sans', sans-serif;
          transition: border 0.2s, box-shadow 0.2s;
          margin-bottom: 10px;
          box-sizing: border-box;
        }
        .inp-login:focus {
          border-color: #F0B323;
          box-shadow: 0 0 0 3px rgba(240, 179, 35, 0.12);
        }
        .inp-login::placeholder { color: #6a5020; }
        .btn-main {
          background: linear-gradient(135deg, #3d2e00, #1a1400);
          color: #f5f0e0;
          border: 1px solid #8a6200;
          border-radius: 12px;
          padding: 13px 20px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          width: 100%;
          margin-bottom: 10px;
          font-family: 'DM Sans', sans-serif;
          transition: transform 0.15s, background 0.2s, box-shadow 0.2s;
        }
        .btn-main:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(240, 179, 35, 0.2);
          background: linear-gradient(135deg, #4d3a00, #241c00);
        }
        .btn-main:active { transform: translateY(0px) scale(0.98); }
        .btn-google {
          background: #0d0e00;
          color: #f5f0e0;
          border: 1px solid #4a3800;
          border-radius: 12px;
          padding: 13px 20px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-family: 'DM Sans', sans-serif;
          transition: transform 0.15s, border-color 0.2s, box-shadow 0.2s;
        }
        .btn-google:hover {
          transform: translateY(-2px);
          border-color: #F0B323;
          box-shadow: 0 8px 24px rgba(0,0,0,0.4);
        }
        .btn-google:active { transform: scale(0.98); }
        .link-btn {
          background: transparent;
          color: #c49020;
          border: none;
          font-size: 13px;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
        }
        .link-btn:hover { color: #F0B323; }
        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255,255,255,0.2);
          border-top-color: #f5f0e0;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          display: inline-block;
        }
      `}</style>

      {/* Logo */}
      <div className="logo-float fade-1" style={{ marginBottom: 16 }}>
        <svg width={64} height={64} viewBox="0 0 100 100" fill="none">
          <rect x="12" y="20" width="7" height="60" fill="#F0B323"/>
          <rect x="12" y="20" width="22" height="7" fill="#F0B323"/>
          <rect x="12" y="73" width="22" height="7" fill="#F0B323"/>
          <rect x="81" y="20" width="7" height="60" fill="#F0B323"/>
          <rect x="66" y="20" width="22" height="7" fill="#F0B323"/>
          <rect x="66" y="73" width="22" height="7" fill="#F0B323"/>
          <rect x="31" y="30" width="7" height="40" fill="#F0B323"/>
          <rect x="62" y="30" width="7" height="40" fill="#F0B323"/>
          <rect x="31" y="46" width="38" height="8" fill="#F0B323"/>
        </svg>
      </div>

      <div className="fade-2" style={{ fontSize: 30, fontWeight: 700, marginBottom: 6 }}>Haulmate</div>
      <div className="fade-2" style={{ fontSize: 14, color: "#c49020", marginBottom: 36 }}>Move smarter, together</div>

      {/* Error */}
      {error && (
        <div className={shake ? "shake" : ""} style={{
          background: "#2a0d0d", border: "1px solid #4a1a1a", borderRadius: 12,
          padding: "12px 16px", color: "#e07070", fontSize: 13,
          marginBottom: 16, width: "100%", boxSizing: "border-box"
        }}>
          {error}
        </div>
      )}

      {/* ── RESET MODE ── */}
      {resetMode ? (
        <div className="fade-3" style={{ width: "100%" }}>
          {resetSent ? (
            <>
              <div style={{ background: "#0d2818", border: "1px solid #1a4a30", borderRadius: 12, padding: "14px 16px", color: "#4caf80", fontSize: 14, marginBottom: 16, lineHeight: 1.5 }}>
                ✓ Reset link sent to <b>{email}</b>. Check your inbox (and spam folder), then come back and sign in with your new password.
              </div>
              <button className="btn-main" onClick={() => { setResetMode(false); setResetSent(false); setError(""); }}>
                Back to Sign In
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, color: "#c49020", marginBottom: 14, lineHeight: 1.5 }}>
                Enter your email and we'll send you a link to reset your password.
              </div>
              <input
                className="inp-login"
                placeholder="Email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(""); }}
              />
              <button className="btn-main" onClick={handleReset} disabled={loading} style={{ marginTop: 6 }}>
                {loading ? <span className="spinner" /> : "Send Reset Link"}
              </button>
              <div style={{ textAlign: "center" }}>
                <button className="link-btn" onClick={() => { setResetMode(false); setError(""); }}>
                  ← Back to sign in
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Inputs */}
          <div className={`fade-3 ${shake ? "shake" : ""}`} style={{ width: "100%" }}>
            <input
              className="inp-login"
              placeholder="Email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(""); }}
            />
            <input
              className="inp-login"
              placeholder="Password"
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(""); }}
              style={{ marginBottom: 8 }}
            />
          </div>

          {/* Forgot password */}
          {!isSignUp && (
            <div className="fade-3" style={{ width: "100%", textAlign: "right", marginBottom: 12 }}>
              <button className="link-btn" onClick={() => { setResetMode(true); setError(""); }}>
                Forgot password?
              </button>
            </div>
          )}

          {/* Sign in button */}
          <div className="fade-4" style={{ width: "100%" }}>
            <button className="btn-main" onClick={handleEmail} disabled={loading}>
              {loading ? <span className="spinner" /> : (isSignUp ? "Create Account" : "Sign In")}
            </button>
          </div>

          {/* Toggle sign up/in */}
          <div className="fade-4">
            <button className="link-btn" style={{ marginBottom: 20 }} onClick={() => { setIsSignUp(!isSignUp); setError(""); }}>
              {isSignUp ? "Already have an account? Sign in" : "Don't have an account? Sign up"}
            </button>
          </div>

          {/* Divider */}
          <div className="fade-5" style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: "#2a2000" }} />
            <span style={{ fontSize: 13, color: "#6a5020" }}>or</span>
            <div style={{ flex: 1, height: 1, background: "#2a2000" }} />
          </div>

          {/* Google button */}
          <div className="fade-6" style={{ width: "100%" }}>
            <button className="btn-google" onClick={handleGoogle} disabled={loading}>
              {loading ? <span className="spinner" /> : (
                <>
                  <svg width={20} height={20} viewBox="0 0 48 48">
                    <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.2 6.5 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 20-9 20-20 0-1.3-.1-2.7-.4-4z"/>
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.1 18.9 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.2 6.5 29.4 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"/>
                    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5l-6.2-5.2C29.4 35.3 26.8 36 24 36c-5.2 0-9.6-2.9-11.3-7.1l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
                    <path fill="#1976D2" d="M43.6 20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.5l6.2 5.2C41 35.2 44 30 44 24c0-1.3-.1-2.7-.4-4z"/>
                  </svg>
                  Continue with Google
                </>
              )}
            </button>
          </div>
        </>
      )}
      <div style={{ marginTop: 32, textAlign: "center" }}>
        <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#6a5020", textDecoration: "none" }}>
          Privacy Policy
        </a>
      </div>
    </div>
  );
}